import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getMyProfile, updateMyProfile, changePassword } from "../../services/user";
import orderService from "../../services/order";
import messageService from "../../services/message";
import { BASE_URL } from "../../services/apiService";
import walletService from "../../services/wallet";
import QRCodeModal from "../../components/QRCodeModal/QRCodeModal";
import "./profile.scss";

const sections = [
  { key: "account", label: "Thông tin tài khoản" },
  { key: "pending", label: "Đơn hàng chờ thanh toán" },
  { key: "completed", label: "Đơn hàng đã mua" },
  { key: "payments", label: "Lịch sử thanh toán" },
  { key: "password", label: "Đổi mật khẩu" },
];

const Profile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({ name: "" });
  const [orders, setOrders] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [activeSection, setActiveSection] = useState("account");
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordStatus, setPasswordStatus] = useState({ type: "", text: "" });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [orderFiles, setOrderFiles] = useState({}); // { orderId: [files] }
  const [selectedQRModal, setSelectedQRModal] = useState(null); // { orderId: qrData }

  useEffect(() => {
    // Check for message from navigation state (e.g., from QR payment page)
    if (location.state?.message) {
      setMessage(location.state.message);
      // Clear the state to avoid showing message again on refresh
      window.history.replaceState({}, document.title);
      // Auto clear message after 5 seconds
      setTimeout(() => setMessage(""), 5000);
    }

    const fetchProfile = async () => {
      try {
        const { data } = await getMyProfile();
        setUser(data);
        setFormData({ name: data.name });

        const ordersData = await orderService.getMyOrders();
        const ordersArray = Array.isArray(ordersData) ? ordersData : [];
        setOrders(ordersArray);

        await fetchWalletInfo();
      } catch (err) {
        setMessage("Vui lòng đăng nhập để xem thông tin.");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [location.state]);

  const fetchWalletInfo = async () => {
    try {
      const { data } = await walletService.getWallet();
      setWallet(data.wallet);
      setTransactions(data.recentTransactions || []);
    } catch (error) {
      console.error("Failed to load wallet", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await updateMyProfile(formData.name, "");
      setUser(data);
      const userInfo = JSON.parse(localStorage.getItem("userInfo"));
      if (userInfo) {
        userInfo.user.name = data.name;
        localStorage.setItem("userInfo", JSON.stringify(userInfo));
      }
      setMessage("Cập nhật thông tin thành công!");
    } catch (err) {
      setMessage("Cập nhật thất bại, vui lòng thử lại.");
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(""), 3000);
    }
  };


  const pendingOrders = useMemo(
    () => orders.filter((order) => order.status === "pending"),
    [orders]
  );

  const completedOrders = useMemo(
    () =>
      orders.filter((order) =>
        ["paid", "completed", "delivered"].includes(order.status)
      ),
    [orders]
  );

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordStatus({ type: "", text: "" });

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordStatus({ type: "error", text: "Mật khẩu mới không khớp" });
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordStatus({
        type: "error",
        text: "Mật khẩu mới phải có ít nhất 6 ký tự",
      });
      return;
    }

    try {
      setPasswordLoading(true);
      await changePassword(passwordForm.oldPassword, passwordForm.newPassword);
      setPasswordStatus({
        type: "success",
        text: "Đổi mật khẩu thành công! Vui lòng đăng nhập lại.",
      });
      setPasswordForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
      setTimeout(() => {
        localStorage.removeItem("userInfo");
        window.location.href = "/login";
      }, 2000);
    } catch (error) {
      setPasswordStatus({
        type: "error",
        text: error?.response?.data?.message || "Không thể đổi mật khẩu",
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  // Load files from messages for each order
  useEffect(() => {
    const loadOrderFiles = async () => {
      const completedOrders = orders.filter((order) =>
        ["paid", "completed", "delivered"].includes(order.status)
      );
      
      const filesMap = {};
      await Promise.all(
        completedOrders.map(async (order) => {
          try {
            const messages = await messageService.getMessagesByOrderId(order._id);
            // Collect all files from messages sent by admin
            const files = [];
            messages.forEach((msg) => {
              if (msg.isFromAdmin && msg.attachments && msg.attachments.length > 0) {
                msg.attachments.forEach((file) => {
                  files.push({
                    ...file,
                    messageId: msg._id,
                    sentAt: msg.createdAt
                  });
                });
              }
            });
            if (files.length > 0) {
              filesMap[order._id] = files;
            }
          } catch (error) {
            console.error(`Failed to load files for order ${order._id}:`, error);
          }
        })
      );
      setOrderFiles(filesMap);
    };

    if (orders.length > 0) {
      loadOrderFiles();
    }
  }, [orders]);

  if (loading) return <div className="loading">Đang tải...</div>;
  if (!user) return <div className="error">{message}</div>;

  const renderOrders = (orderList, emptyLabel) => {
    const safeOrderList = Array.isArray(orderList) ? orderList : [];
    if (safeOrderList.length === 0) {
      return <p className="empty-state">{emptyLabel}</p>;
    }

    const getFileUrl = (fileUrl, filename = null) => {
      // Dùng endpoint download để đảm bảo extension được thêm vào
      if (fileUrl.startsWith("http")) {
        // Cloudinary hoặc external URL
        const downloadUrl = `${BASE_URL}/files/download?fileUrl=${encodeURIComponent(fileUrl)}${filename ? `&filename=${encodeURIComponent(filename)}` : ''}`;
        return downloadUrl;
      }
      // Local file
      const downloadUrl = `${BASE_URL}/files/download?fileUrl=${encodeURIComponent(fileUrl)}${filename ? `&filename=${encodeURIComponent(filename)}` : ''}`;
      return downloadUrl;
    };

    return (
      <div className="orders-list">
        {safeOrderList.map((order) => {
          const orderFilesList = orderFiles[order._id] || [];
          const hasInvoice = order.invoicePath;
          const hasFiles = orderFilesList.length > 0;
          
          return (
            <div key={order._id} className="order-card">
              <div className="order-header">
                <div className="order-id">#{order._id.substring(0, 8)}</div>
                <span className={`status ${order.status}`}>{order.status}</span>
              </div>
              <p className="date">
                {new Date(order.createdAt).toLocaleString("vi-VN")}
              </p>
              <p className="total">
                {new Intl.NumberFormat("vi-VN", {
                  style: "currency",
                  currency: "VND",
                }).format(order.totalAmount)}
              </p>
              {order.paymentDetails?.telegramUsername && (
                <p className="meta">
                  Telegram: <strong>{order.paymentDetails.telegramUsername}</strong>
                </p>
              )}
              <ul className="order-items">
                {Array.isArray(order.items) ? order.items.map((item, i) => {
                  let expiry = null;
                  if (item.durationMonths && order.createdAt) {
                    const d = new Date(order.createdAt);
                    d.setMonth(d.getMonth() + item.durationMonths);
                    expiry = d.toLocaleDateString("vi-VN");
                  }
                  return (
                    <li key={`${order._id}-${i}`}>
                      {item.quantity} × {item.name}
                      {expiry && <span>(HSD: {expiry})</span>}
                      {item.type === "service" && (
                        <div className="order-service-detail">
                          <span>
                            Số lượng: {new Intl.NumberFormat("vi-VN").format(item.serviceQuantity || 0)}{" "}
                            {item.serviceUnitLabel || "lượt"}
                          </span>
                          {item.serviceServer?.name && <span>Server: {item.serviceServer.name}</span>}
                          {item.serviceEmotion && <span>Cảm xúc: {item.serviceEmotion}</span>}
                          {item.serviceUrls && Object.entries(item.serviceUrls).map(([key, value]) => (
                            <a key={key} href={value} target="_blank" rel="noreferrer">
                              {key}: {value}
                            </a>
                          ))}
                        </div>
                      )}
                    </li>
                  );
                }) : (
                  <li>No items in this order</li>
                )}
              </ul>
              {order.customQRCode && (
                <div style={{ marginTop: '15px', marginBottom: '15px' }}>
                  <p className="meta" style={{ marginBottom: '8px' }}>
                    <strong>QR Code thanh toán:</strong>
                  </p>
                  <div
                    onClick={() => setSelectedQRModal({ orderId: order._id, qrData: order.customQRCode })}
                    style={{
                      cursor: 'pointer',
                      display: 'inline-block',
                      border: '2px solid #007bff',
                      borderRadius: '8px',
                      padding: '10px',
                      textAlign: 'center',
                      backgroundColor: '#f8f9fa'
                    }}
                  >
                    <img
                      src={order.customQRCode.imageUrl}
                      alt="QR Code"
                      style={{
                        maxWidth: '150px',
                        maxHeight: '150px',
                        display: 'block',
                        margin: '0 auto'
                      }}
                    />
                    <p style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                      Click để xem thông tin giao dịch
                    </p>
                  </div>
                </div>
              )}
              {["paid","completed","delivered"].includes(order.status) && (
                <div className="order-files-section">
                  {hasInvoice && (
                    <p className="meta">
                      <button
                        onClick={async () => {
                          try {
                            await orderService.downloadInvoice(order._id);
                          } catch (error) {
                            setMessage(error.response?.data?.message || 'Lỗi khi tải invoice');
                            setTimeout(() => setMessage(""), 3000);
                          }
                        }}
                        style={{ 
                          display: "inline-block", 
                          marginRight: "10px",
                          padding: "5px 10px",
                          backgroundColor: "#007bff",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "14px"
                        }}
                      >
                        📄 Tải hóa đơn (PDF)
                      </button>
                    </p>
                  )}
                  {hasFiles && (
                    <div className="order-files">
                      <p className="meta" style={{ marginTop: hasInvoice ? "8px" : "0" }}>
                        <strong>Files từ người bán:</strong>
                      </p>
                      {orderFilesList.map((file, idx) => (
                        <p key={`${order._id}-file-${idx}`} className="meta">
                          <a
                            href={getFileUrl(file.url, file.originalName)}
                            download={file.originalName}
                            style={{ display: "inline-block", marginLeft: "10px" }}
                          >
                            📎 {file.originalName}
                          </a>
                          <span style={{ fontSize: "0.85em", color: "#999", marginLeft: "8px" }}>
                            ({new Date(file.sentAt).toLocaleString("vi-VN")})
                          </span>
                        </p>
                      ))}
                    </div>
                  )}
                  {!hasInvoice && !hasFiles && (
                    <p className="meta">
                      <span style={{ color: "#666", fontStyle: "italic" }}>
                     
                      </span>
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeSection) {
      case "account":
        return (
          <>
            <section className="section-card">
              <div className="section-header">
                <div>
                  <h2>Thông tin tài khoản</h2>
                  <p>Cập nhật tên hiển thị và theo dõi số dư ví</p>
                </div>
                <button onClick={fetchWalletInfo} className="outline-btn small">
                  Làm mới ví
                </button>
              </div>

              {message && (
                <div className={`alert ${message.includes("thành công") ? "success" : ""}`}>
                  {message}
                </div>
              )}

              <div className="account-grid">
                <div className="account-card">
                  <h4>Thông tin cá nhân</h4>
                  <form onSubmit={handleSubmit}>
                    <div className="form-group">
                      <label>Họ và tên</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <input type="email" value={user.email} disabled />
                    </div>
                    <button type="submit" className="primary-btn" disabled={saving}>
                      {saving ? "Đang lưu..." : "Lưu thay đổi"}
                    </button>
                  </form>
                </div>

                <div className="account-card">
                  <h4>Số dư ví</h4>
                  <div className="wallet-balance">
                    <strong>
                      {wallet
                        ? new Intl.NumberFormat("vi-VN", {
                            style: "currency",
                            currency: "VND",
                          }).format(wallet.balance)
                        : "--"}
                    </strong>
                    <p>Ví tự động cập nhật khi giao dịch thành công</p>
                  </div>
                  <button 
                    className="primary-btn" 
                    onClick={() => navigate("/qr-payment")}
                    style={{ width: "100%", marginTop: "1rem" }}
                  >
                    Tạo yêu cầu nạp tiền
                  </button>

                </div>
              </div>
            </section>
          </>
        );
      case "pending":
        return (
          <section className="section-card">
            <div className="section-header">
              <div>
                <h2>Đơn hàng chờ thanh toán</h2>
                <p>Theo dõi các đơn đang ở trạng thái pending</p>
              </div>
            </div>
            {renderOrders(pendingOrders, "Bạn chưa có đơn hàng chờ thanh toán.")}
          </section>
        );
      case "completed":
        return (
          <section className="section-card">
            <div className="section-header">
              <div>
                <h2>Đơn hàng đã mua</h2>
                <p>Lịch sử các đơn đã thanh toán / hoàn tất</p>
              </div>
            </div>
            {renderOrders(completedOrders, "Bạn chưa có đơn hàng đã hoàn tất.")}
          </section>
        );
      case "payments":
        return (
          <section className="section-card">
            <div className="section-header">
              <div>
                <h2>Lịch sử thanh toán</h2>
                <p>Gồm các giao dịch nạp ví gần đây nhất</p>
              </div>
              <button onClick={fetchWalletInfo} className="outline-btn small">
                Làm mới
              </button>
            </div>
            {transactions.length === 0 ? (
              <p className="empty-state">Chưa có giao dịch nào.</p>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Mã giao dịch</th>
                      <th>Số tiền</th>
                      <th>Phương thức</th>
                      <th>Trạng thái</th>
                      <th>Ngày tạo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => (
                      <tr key={tx._id}>
                        <td>{tx.referenceCode}</td>
                        <td>
                          {new Intl.NumberFormat("vi-VN", {
                            style: "currency",
                            currency: "VND",
                          }).format(tx.amount)}
                        </td>
                        <td>{tx.provider === "sepay" ? "Sepay" : tx.method || "—"}</td>
                        <td>
                          <span className={`status ${tx.status}`}>{tx.status}</span>
                        </td>
                        <td>{new Date(tx.createdAt).toLocaleString("vi-VN")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        );
      case "password":
        return (
          <section className="section-card">
            <div className="section-header">
              <div>
                <h2>Đổi mật khẩu</h2>
                <p>Đảm bảo tài khoản của bạn luôn an toàn</p>
              </div>
            </div>

            {passwordStatus.text && (
              <div className={`alert ${passwordStatus.type === "success" ? "success" : ""}`}>
                {passwordStatus.text}
              </div>
            )}

            <form className="password-form" onSubmit={handlePasswordSubmit}>
              <div className="form-group">
                <label>Mật khẩu hiện tại</label>
                <input
                  type="password"
                  name="oldPassword"
                  value={passwordForm.oldPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Mật khẩu mới</label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Nhập lại mật khẩu mới</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>
              <button type="submit" className="primary-btn" disabled={passwordLoading}>
                {passwordLoading ? "Đang xử lý..." : "Đổi mật khẩu"}
              </button>
            </form>
          </section>
        );
      default:
        return null;
    }
  };

  return (
    <div className="profile-container">
      <div className="profile-dashboard">
        <aside className="profile-sidebar">
          <div className="sidebar-card">
            <div className="avatar-placeholder">
              {user?.name?.charAt(0).toUpperCase() || "U"}
            </div>
            <div className="user-info">
              <strong>{user.name}</strong>
              <p>{user.email}</p>
              <span className="role-chip">{user.role}</span>
            </div>
          </div>
          <ul className="profile-menu">
            {sections.map((section) => (
              <li
                key={section.key}
                className={section.key === activeSection ? "active" : ""}
                onClick={() => setActiveSection(section.key)}
              >
                {section.label}
              </li>
            ))}
          </ul>
        </aside>
        <main className="profile-content">{renderContent()}</main>
      </div>
      {selectedQRModal && (
        <QRCodeModal
          qrData={selectedQRModal.qrData}
          onClose={() => setSelectedQRModal(null)}
        />
      )}
    </div>
  );
};

export default Profile;
